import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import Header from "./Header";
import Sidebar from "./Sidebar";
import CustomTargetConfig from "./targets/CustomTargetConfig";
import { CUSTOM_TARGET_PY_SCRIPTS } from "./targets/customTargetHelpers";
import RunTestModal from "./testStatus/RunTestModal";
import ScheduleTestModal from "./testStatus/ScheduleTestModal";
import StatusResultModal from "./StatusResultModal";
import {
  aiAppTypeLabels,
  industryLabels,
  subIndustryLabels,
  quickStartTestLabels,
} from "../utils/categoryLabels";
import { getApiUrl } from "../utils/apiHelper";
import { runQuickStart } from "../utils/quickStartRunTests";
import "../styles/theme.css";
import "../styles/dashboard.css";
import "../styles/ProfilesManager.css";
import "../styles/Generate.css";
import "../styles/QuickStart.css";
import { useSidebar } from "../App";

const STEPS = [
  { id: 1, titleKey: "stepIndustry" },
  { id: 2, titleKey: "stepAiTarget" },
  { id: 3, titleKey: "stepTests" },
];

const DEFAULT_INDUSTRY = Object.keys(industryLabels)[0];

/** Maps Quick Start checkbox keys → license allowedTestTypes profile keys. */
const QUICK_START_TEST_LICENSE_MAP = {
  red_teaming_prompts: "prompts_docs_llm",
  malicious_docs: "prompts_docs_llm",
  ai_specific_dos: "dos_tester",
  regulation_compliance: "regulation_compliance",
};

const DEFAULT_ALLOWED_TEST_TYPES = {
  prompts_docs_llm: { allowed: true },
  ai_model_attack_surface: { allowed: true },
  mcp_tester: { allowed: true },
  dos_tester: { allowed: true },
  rto: { allowed: true },
  regulation_compliance: { allowed: true },
};

export default function QuickStart() {
  const { t } = useTranslation("quickStart");
  const { sidebarCollapsed } = useSidebar();
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState(0);
  const [showChoiceModal, setShowChoiceModal] = useState(true);
  const [showRunTestModal, setShowRunTestModal] = useState(false);
  const [showScheduleModal, setShowScheduleModal] = useState(false);
  const [selectedProfileIdForSchedule, setSelectedProfileIdForSchedule] = useState(null);

  // Step 1
  const [aiAppType, setAiAppType] = useState(Object.keys(aiAppTypeLabels)[0]);
  const [industry, setIndustry] = useState(DEFAULT_INDUSTRY);
  const [subIndustry, setSubIndustry] = useState(
    Object.keys(subIndustryLabels[DEFAULT_INDUSTRY])[0]
  );

  // Step 2
  const [targetName, setTargetName] = useState("");
  const [targetUrl, setTargetUrl] = useState("");
  const [apiConfigMode, setApiConfigMode] = useState(CUSTOM_TARGET_PY_SCRIPTS);
  const [advancedOptions, setAdvancedOptions] = useState(null);
  const [inputScriptFile, setInputScriptFile] = useState(null);
  const [outputScriptFile, setOutputScriptFile] = useState(null);
  const [requestParams, setRequestParams] = useState("{}");
  const [responseParams, setResponseParams] = useState("{}");
  const [promptsProcessing, setPromptsProcessing] = useState("single");

  // Step 3
  const [tests, setTests] = useState(
    Object.fromEntries(Object.keys(quickStartTestLabels).map((key) => [key, false]))
  );
  const [reportName, setReportName] = useState("");
  const [runLoading, setRunLoading] = useState(false);
  const [allowedTestTypes, setAllowedTestTypes] = useState(DEFAULT_ALLOWED_TEST_TYPES);
  const [statusModal, setStatusModal] = useState({ open: false, type: "error", message: "" });

  const subIndustries = subIndustryLabels[industry] || {};
  const isFirst = currentStep === 0;
  const isLast = currentStep === STEPS.length - 1;

  const isQuickStartTestAllowed = (testKey) => {
    const licenseKey = QUICK_START_TEST_LICENSE_MAP[testKey];
    if (!licenseKey) return true;
    return allowedTestTypes[licenseKey]?.allowed !== false;
  };

  useEffect(() => {
    let cancelled = false;

    const fetchAllowedTestTypes = async () => {
      try {
        const response = await fetch(getApiUrl("/api/internal/license/allowedTestTypes/"), {
          method: "GET",
          credentials: "include",
        });
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }
        const data = await response.json();
        if (!data.success || !data.allowed_test_types) {
          throw new Error("Invalid API response format");
        }
        if (cancelled) return;

        setAllowedTestTypes(data.allowed_test_types);
        setTests((prev) => {
          const next = { ...prev };
          Object.keys(quickStartTestLabels).forEach((testKey) => {
            const licenseKey = QUICK_START_TEST_LICENSE_MAP[testKey];
            if (licenseKey && data.allowed_test_types[licenseKey]?.allowed === false) {
              next[testKey] = false;
            }
          });
          return next;
        });
      } catch (error) {
        console.error("Error fetching allowed test types:", error);
        if (!cancelled) {
          setAllowedTestTypes(DEFAULT_ALLOWED_TEST_TYPES);
        }
      }
    };

    fetchAllowedTestTypes();
    return () => {
      cancelled = true;
    };
  }, []);

  const handleRunTests = async () => {
    if (runLoading) return;

    const allowedTests = Object.fromEntries(
      Object.entries(tests).map(([key, selected]) => [
        key,
        selected && isQuickStartTestAllowed(key),
      ])
    );

    setRunLoading(true);
    try {
      await runQuickStart({
        aiAppType,
        industry,
        subIndustry,
        target: {
          name: targetName,
          url: targetUrl,
          apiConfigMode,
          promptsProcessing,
          inputScriptFile,
          outputScriptFile,
          requestParams,
          responseParams,
          advancedOptions,
        },
        tests: allowedTests,
        reportName,
      });
      navigate("/status");
    } catch (error) {
      setStatusModal({
        open: true,
        type: "error",
        message: error.message || t("quickStartFailed"),
      });
      setRunLoading(false);
    }
  };

  return (
    <div>
      <Header />
      <StatusResultModal
        open={statusModal.open}
        type={statusModal.type}
        message={statusModal.message}
        onClose={() => setStatusModal({ open: false, type: "error", message: "" })}
      />
      {showChoiceModal && (
        <div
          className="modal-backdrop"
          style={{ zIndex: 9999 }}
        >
          <div
            className="modal-content"
            style={{ animation: "fadeIn 0.3s ease-out" }}
          >
            <h3>{t("howWouldYouLikeToStart")}</h3>
            <p
              style={{
                margin: "0 0 24px",
                color: "var(--muted-text)",
                fontSize: "14px",
                lineHeight: 1.5,
                textAlign: "left",
              }}
            >
              {t("choiceDescription")}
            </p>
            <div
              style={{
                display: "flex",
                gap: "8px",
                justifyContent: "center",
                flexWrap: "wrap",
              }}
            >
              <button
                type="button"
                className="btn btn-primary"
                onClick={() => setShowChoiceModal(false)}
              >
                {t("quickStart")}
              </button>
              <button
                type="button"
                className="btn btn-secondary"
                onClick={() => {
                  setShowChoiceModal(false);
                  setShowRunTestModal(true);
                }}
              >
                {t("existingProfiles")}
              </button>
            </div>
          </div>
        </div>
      )}
      <RunTestModal
        isOpen={showRunTestModal}
        onClose={() => setShowRunTestModal(false)}
        onTestStarted={(jobId) => {
          navigate("/status", { state: { jobId } });
        }}
        onScheduleTest={(profileId) => {
          setSelectedProfileIdForSchedule(profileId);
          setShowScheduleModal(true);
        }}
      />
      <ScheduleTestModal
        isOpen={showScheduleModal}
        onClose={() => {
          setShowScheduleModal(false);
          setSelectedProfileIdForSchedule(null);
        }}
        selectedProfileId={selectedProfileIdForSchedule}
        onScheduleCreated={() => {}}
      />
      <div className="main-container">
        <div className={`${sidebarCollapsed ? "col-1" : "col-2"} p-0`}>
          <Sidebar />
        </div>
        <main className={`qs-main ${sidebarCollapsed ? "col-11" : "col-10"}`}>
          <div className="dashboard qs-page">
            <div className="profile qs-page-title">
              <h1>{t("title")}</h1>
            </div>

            <div className="qs-wizard">
              <div className="qs-stepper" role="navigation" aria-label={t("stepsAriaLabel")}>
                {STEPS.map((step, index) => {
                  const status =
                    index < currentStep
                      ? "completed"
                      : index === currentStep
                        ? "active"
                        : "upcoming";
                  return (
                    <React.Fragment key={step.id}>
                      {index > 0 && (
                        <div
                          className={`qs-stepper-line ${index <= currentStep ? "filled" : ""}`}
                        />
                      )}
                      <button
                        type="button"
                        className={`qs-stepper-item ${status}`}
                        onClick={() => setCurrentStep(index)}
                        aria-current={index === currentStep ? "step" : undefined}
                      >
                        <span className="qs-stepper-number">{step.id}</span>
                        <span className="qs-stepper-label">{t(step.titleKey)}</span>
                      </button>
                    </React.Fragment>
                  );
                })}
              </div>

              <div className="qs-panel">
                {currentStep === 0 && (
                  <div className="qs-screen">
                    <div className="qs-form-grid">
                      <label className="pm-input-label qs-field" htmlFor="qs-ai-app-type">
                        <span>{t("aiAppType")}</span>
                        <select
                          id="qs-ai-app-type"
                          className="pm-input qs-select"
                          value={aiAppType}
                          onChange={(e) => setAiAppType(e.target.value)}
                        >
                          {Object.entries(aiAppTypeLabels).map(([value, label]) => (
                            <option key={value} value={value}>
                              {label}
                            </option>
                          ))}
                        </select>
                      </label>

                      <div className="qs-field-row">
                        <label className="pm-input-label qs-field" htmlFor="qs-industry">
                          <span>{t("industry")}</span>
                          <select
                            id="qs-industry"
                            className="pm-input qs-select"
                            value={industry}
                            onChange={(e) => {
                              const next = e.target.value;
                              setIndustry(next);
                              setSubIndustry(Object.keys(subIndustryLabels[next] || {})[0] || "");
                            }}
                          >
                            {Object.entries(industryLabels).map(([value, label]) => (
                              <option key={value} value={value}>
                                {label}
                              </option>
                            ))}
                          </select>
                        </label>

                        <label className="pm-input-label qs-field" htmlFor="qs-sub-industry">
                          <span>{t("subIndustry")}</span>
                          <select
                            id="qs-sub-industry"
                            className="pm-input qs-select"
                            value={subIndustry}
                            onChange={(e) => setSubIndustry(e.target.value)}
                          >
                            {Object.entries(subIndustries).map(([value, label]) => (
                              <option key={value} value={value}>
                                {label}
                              </option>
                            ))}
                          </select>
                        </label>
                      </div>
                    </div>
                  </div>
                )}

                {currentStep === 1 && (
                  <div className="qs-screen">
                    <div className="qs-form-stack">
                      <label className="pm-input-label">
                        <div className="qs-label-row">
                          <span>{t("name")}</span>
                          <span className="qs-char-count">
                            {targetName.length} / 100
                          </span>
                        </div>
                        <input
                          className="pm-input"
                          value={targetName}
                          onChange={(e) => setTargetName(e.target.value)}
                          placeholder={t("enterTargetName")}
                          maxLength={100}
                        />
                      </label>

                      <CustomTargetConfig
                        className="qs-script-json"
                        url={targetUrl}
                        onUrlChange={(e) => setTargetUrl(e.target.value)}
                        mode={apiConfigMode}
                        onModeChange={setApiConfigMode}
                        inputScriptFile={inputScriptFile}
                        outputScriptFile={outputScriptFile}
                        onInputScriptChange={setInputScriptFile}
                        onOutputScriptChange={setOutputScriptFile}
                        promptsProcessing={promptsProcessing}
                        onPromptsProcessingChange={setPromptsProcessing}
                        requestParams={requestParams}
                        responseParams={responseParams}
                        onRequestParamsChange={(e) => setRequestParams(e.target.value)}
                        onResponseParamsChange={(e) => setResponseParams(e.target.value)}
                        onAdvancedOptionsChange={setAdvancedOptions}
                      />
                    </div>
                  </div>
                )}

                {currentStep === 2 && (
                  <div className="qs-screen">
                    <div className="qs-form-stack">
                      <label className="pm-input-label">
                        <div className="qs-label-row">
                          <span>{t("reportName")}</span>
                        </div>
                        <input
                          className="pm-input"
                          value={reportName}
                          onChange={(e) => setReportName(e.target.value)}
                          placeholder={t("enterReportNameOptional")}
                        />
                      </label>

                      <div className="qs-checkbox-list" role="group" aria-label={t("testTypes")}>
                        {Object.entries(quickStartTestLabels).map(([value, label]) => {
                          const allowed = isQuickStartTestAllowed(value);
                          return (
                            <label
                              key={value}
                              className={`qs-checkbox-item${!allowed ? " is-disabled" : ""}`}
                            >
                              <input
                                type="checkbox"
                                checked={tests[value]}
                                disabled={!allowed}
                                onChange={() => {
                                  if (!allowed) return;
                                  setTests((prev) => ({ ...prev, [value]: !prev[value] }));
                                }}
                              />
                              <span>{label}</span>
                            </label>
                          );
                        })}
                      </div>

                      <div className="qs-run-row">
                        <button
                          type="button"
                          className="btn"
                          onClick={handleRunTests}
                          disabled={runLoading}
                        >
                          {runLoading ? t("running") : t("runTests")}
                        </button>
                      </div>
                    </div>
                  </div>
                )}
              </div>

              <div className="qs-actions">
                {!isFirst && (
                  <button
                    type="button"
                    className="btn qs-btn-secondary"
                    onClick={() => setCurrentStep((s) => s - 1)}
                  >
                    {t("back")}
                  </button>
                )}
                {!isLast && (
                  <button
                    type="button"
                    className="btn"
                    onClick={() => setCurrentStep((s) => s + 1)}
                  >
                    {t("next")}
                  </button>
                )}
              </div>
            </div>
          </div>
        </main>
      </div>
    </div>
  );
}
